import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';

const TrainerSimulations = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [protocols, setProtocols] = useState([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'date');
  const [order, setOrder] = useState(searchParams.get('order') || 'DESC');

  useEffect(() => {
    fetchProtocols();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sort, order]);

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...(search && { search }),
        sort,
        order
      });
      
      const response = await API.get(`/qurator/protocols/trainer?${params}`);
      if (response.data.success) {
        setProtocols(response.data.protocols || []);
        console.log('Trainer simulations loaded:', response.data.protocols?.length || 0);
      } else {
        console.error('Failed to fetch trainer simulations:', response.data.message);
        setProtocols([]);
      }
    } catch (error) {
      console.error('Error fetching trainer simulations:', error);
      console.error('Error details:', error.response?.data);
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() });
  };

  const handleSort = (column) => {
    // Map frontend column names to match PHP logic
    const columnMap = {
      'title': 'title',
      'description': 'description',
      'date': 'date'
    };
    const mappedColumn = columnMap[column] || 'date';
    
    const newOrder = sort === mappedColumn && order === 'DESC' ? 'ASC' : 'DESC';
    setSort(mappedColumn);
    setOrder(newOrder);
    
    const params = new URLSearchParams(searchParams);
    params.set('sort', mappedColumn);
    params.set('order', newOrder);
    navigate({ search: params.toString() });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this simulation?')) {
      try {
        const response = await API.delete(`/qurator/protocols/${id}`);
        if (response.data.success) {
          fetchProtocols();
        }
      } catch (error) {
        console.error('Error deleting protocol:', error);
        alert('Failed to delete simulation');
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSortIcon = (column) => {
    // Map display column to sort column
    const columnMap = {
      'title': 'title',
      'description': 'description',
      'date': 'date'
    };
    const mappedColumn = columnMap[column] || 'date';
    
    if (sort !== mappedColumn) return '↕';
    return order === 'ASC' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="simulations-loading">
          <div className="qrt-loading"></div>
          <p>Loading simulations...</p>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="container-fluid px-4 py-4">
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h4 className="mb-1">Trainer Simulations</h4>
              <p className="mb-0">Manage your trainer simulations</p>
            </div>
            <Link to="/drafts?project_id=0&type=mr" className="btn btn-light">
              <i className="bi bi-plus-lg me-2"></i>New Simulation
            </Link>
          </div>
        </div>

        <div className="main-card">
          <div className="p-3 border-bottom">
            <div className="search-container">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                className="search-input"
                placeholder="Search simulations..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="table-responsive">
            <table className="table" id="protocolTable">
              <thead>
                <tr>
                  <th 
                    className={`sort-header ${sort === 'title' ? order.toLowerCase() : ''}`}
                    onClick={() => handleSort('title')}
                    style={{ cursor: 'pointer' }}
                  >
                    Title {getSortIcon('title')}
                  </th>
                  <th className="description-cell">Description</th>
                  <th 
                    className={`sort-header date-cell ${sort === 'date' ? order.toLowerCase() : ''}`}
                    onClick={() => handleSort('date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Date {getSortIcon('date')}
                  </th>
                  <th className="text-end" style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {protocols.length > 0 ? (
                  protocols.map((protocol) => (
                    <tr key={protocol.prt_id}>
                      <td>{protocol.draft_title}</td>
                      <td className="description-cell">
                        {protocol.prt_description 
                          ? (protocol.prt_description.length > 100 
                              ? protocol.prt_description.substring(0, 100) + '...'
                              : protocol.prt_description)
                          : '-'}
                      </td>
                      <td className="date-cell">{formatDate(protocol.date)}</td>
                      <td>
                        <div className="action-wrapper">
                          <Link
                            to={`/draft-read?viewid=${protocol.prt_id}`}
                            className="action-btn view-btn"
                            title="View"
                          >
                            <i className="bi bi-eye"></i>
                          </Link>
                          <Link
                            to={`/draft-edit?editid=${protocol.prt_id}`}
                            className="action-btn edit-btn"
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                          <button
                            type="button"
                            className="action-btn delete-btn"
                            title="Delete"
                            onClick={() => handleDelete(protocol.prt_id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-5">
                      <div className="empty-state">
                        <i className="bi bi-inbox empty-state-icon"></i>
                        <div className="empty-state-title">No simulations found</div>
                        <div className="empty-state-text">
                          {search ? 'Try adjusting your search' : 'Create your first simulation to get started'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </QuratorLayout>
  );
};

export default TrainerSimulations;
